import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Phone, Mail, MapPin, Clock, ShieldCheck } from "lucide-react";
import { COMPANY } from "../data/site";

/* eslint-disable react/no-unescaped-entities */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Concrete Patio",
  "Stamped / Decorative Concrete",
  "Concrete Driveway",
  "Concrete Repair",
  "Foundation",
  "Retaining Wall",
  "Masonry",
  "Pool Deck",
  "Xeriscape Design",
  "Irrigation / Drip",
  "Sod / Artificial Turf",
  "Outdoor Lighting",
  "Fire Pit / Outdoor Living",
  "Other",
];

export default function ContactForm({ compact = false }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "Concrete Patio",
    location: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Thanks! We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", phone: "", service: "Concrete Patio", location: "", message: "" });
    } catch (err) {
      toast.error("Could not send. Please call us at " + COMPANY.phone);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="contact-section"
      className={`bg-white ${compact ? "py-16" : "py-20 md:py-28 border-t border-stone-200"}`}
      id="contact"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-red-600">
              Free, in-writing estimate
            </div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-black tracking-tighter text-stone-900 leading-[1.02]">
              Let's build it strong.
            </h2>
            <p className="mt-5 text-stone-600 text-lg leading-relaxed">
              Tell us about your project. We'll visit your property within 24–48 hours, take
              measurements and send a written estimate — no pressure, no fine print.
            </p>

            <div className="mt-9 space-y-5">
              <a
                href={COMPANY.phoneTel}
                data-testid="contact-call-link"
                className="flex items-start gap-4 group"
              >
                <div className="bg-red-600 text-white p-3"><Phone size={18} /></div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-bold">Call or text</div>
                  <div className="font-display text-2xl font-black tracking-tight text-stone-900 group-hover:text-red-600">
                    {COMPANY.phone}
                  </div>
                </div>
              </a>
              <a
                href={COMPANY.emailMailto}
                data-testid="contact-email-link"
                className="flex items-start gap-4 group"
              >
                <div className="bg-stone-900 text-white p-3"><Mail size={18} /></div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-bold">Email</div>
                  <div className="font-bold text-lg text-stone-900 group-hover:text-red-600">
                    {COMPANY.email}
                  </div>
                </div>
              </a>
              <div className="flex items-start gap-4" data-testid="contact-office-address">
                <div className="bg-stone-200 text-stone-900 p-3"><MapPin size={18} /></div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-bold">Office</div>
                  <div className="font-semibold text-stone-900">{COMPANY.address}</div>
                  <div className="font-semibold text-stone-900">Austin, TX {COMPANY.zip}</div>
                  <div className="mt-1 text-sm text-stone-600">Serving Austin TX + surrounding suburbs &amp; Hill Country</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-stone-200 text-stone-900 p-3"><Clock size={18} /></div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-bold mb-2">Hours</div>
                  <ul className="space-y-1 text-sm">
                    {COMPANY.hoursList.map((h) => {
                      const closed = h.time === "Closed";
                      return (
                        <li key={h.day} className="flex justify-between max-w-[260px]">
                          <span className="font-semibold text-stone-900">{h.day}</span>
                          <span className={closed ? "text-red-600 font-semibold" : "text-stone-700"}>
                            {h.time}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-10 pt-6 border-t border-stone-200 flex items-center gap-2 text-xs text-stone-500">
              <ShieldCheck size={14} className="text-emerald-700" />
              Bonded & insured in Texas. Free estimates always.
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-stone-900 text-white p-8 md:p-10 border border-stone-900"
            >
              {done ? (
                <div className="py-16 text-center" data-testid="contact-form-success">
                  <div className="font-display text-3xl font-black tracking-tight">Got it.</div>
                  <p className="mt-3 text-stone-300">
                    A TSB project manager will reach out within 24 hours. For urgent jobs, call{" "}
                    <a href={COMPANY.phoneTel} className="text-red-500 font-bold underline">{COMPANY.phone}</a>.
                  </p>
                  <button
                    type="button"
                    onClick={() => setDone(false)}
                    className="mt-8 text-xs uppercase tracking-[0.2em] font-bold text-stone-400 hover:text-white"
                  >
                    Submit another request →
                  </button>
                </div>
              ) : (
                <>
                  <div className="text-xs uppercase tracking-[0.22em] font-bold text-red-500 mb-6">
                    Project request
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Name" name="name" value={form.name} onChange={update("name")} required testId="form-name" />
                    <Field label="Phone" name="phone" value={form.phone} onChange={update("phone")} required testId="form-phone" />
                    <Field label="Email" name="email" type="email" value={form.email} onChange={update("email")} required className="sm:col-span-2" testId="form-email" />
                    <div className="sm:col-span-2">
                      <Label htmlFor="form-service">Service interest</Label>
                      <select
                        id="form-service"
                        value={form.service}
                        onChange={update("service")}
                        data-testid="form-service"
                        aria-label="Service interest"
                        className="w-full bg-stone-800 border border-stone-700 px-4 py-3 text-white focus:outline-none focus:border-red-600"
                      >
                        {SERVICES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <Field label="Project city / neighborhood" name="location" value={form.location} onChange={update("location")} className="sm:col-span-2" testId="form-location" placeholder="e.g. Round Rock, Mueller, Westlake…" />
                    <div className="sm:col-span-2">
                      <Label htmlFor="form-message">Tell us about your project</Label>
                      <textarea
                        id="form-message"
                        required
                        value={form.message}
                        onChange={update("message")}
                        rows={5}
                        data-testid="form-message"
                        aria-label="Tell us about your project"
                        placeholder="Approx. size, timeline, what you'd like done…"
                        className="w-full bg-stone-800 border border-stone-700 px-4 py-3 text-white focus:outline-none focus:border-red-600 resize-none"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-form-submit"
                    className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-bold py-4 px-6 transition-colors"
                  >
                    {loading ? "Sending…" : <>Send Project Request <Send size={16} /></>}
                  </button>
                  <p className="mt-4 text-xs text-stone-400">
                    By submitting, you agree to be contacted by Texan Strong Builds about your project. We never share your information.
                  </p>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Label({ children, htmlFor }) {
  return <label htmlFor={htmlFor} className="block text-xs uppercase tracking-[0.18em] font-bold text-stone-400 mb-2">{children}</label>;
}

function Field({ label, type = "text", value, onChange, required, className = "", testId, placeholder }) {
  const inputId = testId || `field-${label.toLowerCase().replace(/[^a-z]/g, "-")}`;
  return (
    <div className={className}>
      <Label htmlFor={inputId}>{label}</Label>
      <input
        id={inputId}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testId}
        aria-label={label}
        className="w-full bg-stone-800 border border-stone-700 px-4 py-3 text-white placeholder-stone-500 focus:outline-none focus:border-red-600"
      />
    </div>
  );
}
